<template>
  <div v-if="modelValue" class="ac-overlay" @click.self="close">
    <div class="ac-enroll" role="dialog" aria-modal="true">
      <button type="button" class="ac-enroll__close" aria-label="关闭" @click="close">×</button>
      <div class="ac-enroll__course">
        <p class="ac-enroll__eyebrow">{{ course?.shortTitle || '课程报名' }}</p>
        <h2>{{ course?.title || trainingMeta.title }}</h2>
        <p class="ac-enroll__sub">
          {{ course?.duration || trainingMeta.duration }}
          ·
          {{ trainingMeta.mode.split('（')[0] }}
        </p>
        <p class="ac-enroll__price-row">
          <span>课程报价</span>
          <strong>{{ displayPrice }}</strong>
          <em>/ 人</em>
        </p>
      </div>
      <form class="ac-enroll__form" @submit.prevent="submit">
        <label class="ac-enroll__row">
          <span>姓名 <em>*</em></span>
          <input v-model.trim="form.name" type="text" placeholder="请输入真实姓名" />
        </label>
        <label class="ac-enroll__row">
          <span>手机号 <em>*</em></span>
          <input v-model.trim="form.phone" type="tel" placeholder="便于确认报名信息" maxlength="11" />
        </label>
        <label class="ac-enroll__row">
          <span>单位 / 学校</span>
          <input v-model.trim="form.organization" type="text" placeholder="选填" />
        </label>
        <label class="ac-enroll__row">
          <span>身份</span>
          <select v-model="form.role">
            <option v-for="role in roleOptions" :key="role" :value="role">{{ role }}</option>
          </select>
        </label>
        <label class="ac-enroll__row ac-enroll__row--top">
          <span>备注</span>
          <textarea v-model.trim="form.note" rows="3" placeholder="同行人数、住宿需求等可在此说明"></textarea>
        </label>
        <p class="ac-enroll__tip">
          提交后我们将尽快联系您。咨询热线：
          <a :href="`tel:${company.phone}`">{{ company.phone }}</a>
        </p>
        <button type="submit" class="ac-enroll__submit">确认报名</button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { trainingMeta, formatCoursePrice } from '../data/trainingCatalog'
import { company } from '../data/siteConfig'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  course: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue'])

const STORAGE_KEY = 'gaoren-training-enrollments'
const roleOptions = ['企业员工', '院校教师', '在校学生', '其他']

const displayPrice = computed(() =>
  formatCoursePrice(props.course?.price ?? trainingMeta.price)
)

const form = reactive({
  name: '',
  phone: '',
  organization: '',
  role: roleOptions[0],
  note: '',
})

watch(
  () => props.modelValue,
  (open) => {
    document.body.style.overflow = open ? 'hidden' : ''
    if (open) {
      form.name = ''
      form.phone = ''
      form.organization = ''
      form.role = roleOptions[0]
      form.note = ''
    }
  }
)

function close() {
  emit('update:modelValue', false)
}

function submit() {
  if (!form.name || !form.phone) {
    ElMessage.warning('请填写姓名和手机号')
    return
  }
  if (!/^1\d{10}$/.test(form.phone)) {
    ElMessage.warning('请输入有效的11位手机号')
    return
  }

  const title = props.course?.title || trainingMeta.title
  const record = {
    id: `${Date.now()}`,
    courseId: props.course?.id || 'vision-10day',
    courseTitle: title,
    name: form.name,
    phone: form.phone,
    organization: form.organization,
    role: form.role,
    note: form.note,
    createdAt: new Date().toISOString(),
  }

  try {
    const prev = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]')
    prev.unshift(record)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prev.slice(0, 100)))
  } catch {
    /* ignore */
  }

  ElMessage.success(`「${title}」报名已提交，我们会尽快联系您`)
  close()
}
</script>

<style scoped>
.ac-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.45);
}

.ac-enroll {
  position: relative;
  width: min(520px, 100%);
  max-height: min(90vh, 760px);
  overflow: auto;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.ac-enroll__close {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: transparent;
  font-size: 22px;
  color: #999;
  cursor: pointer;
}

.ac-enroll__course {
  padding: 22px 22px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafbfd;
}

.ac-enroll__eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  color: #2d8cf0;
  font-weight: 600;
}

.ac-enroll__course h2 {
  margin: 0 0 6px;
  font-size: 16px;
  line-height: 1.35;
  color: #222;
  padding-right: 28px;
}

.ac-enroll__sub {
  margin: 0;
  font-size: 12px;
  color: #999;
}

.ac-enroll__price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 10px 0 0;
}

.ac-enroll__price-row strong {
  font-size: 22px;
  color: #ff6a00;
}

.ac-enroll__price-row span {
  font-size: 12px;
  color: #999;
}

.ac-enroll__price-row em {
  font-style: normal;
  font-size: 12px;
  color: #999;
}

.ac-enroll__form {
  padding: 8px 22px 22px;
}

.ac-enroll__row {
  display: grid;
  grid-template-columns: 88px 1fr;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  font-size: 13px;
  color: #555;
}

.ac-enroll__row--top {
  align-items: start;
}

.ac-enroll__row--top span {
  padding-top: 10px;
}

.ac-enroll__row em {
  color: #f56c6c;
  font-style: normal;
}

.ac-enroll__row input,
.ac-enroll__row select,
.ac-enroll__row textarea {
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font: inherit;
  background: #fff;
  box-sizing: border-box;
}

.ac-enroll__row input:focus,
.ac-enroll__row select:focus,
.ac-enroll__row textarea:focus {
  outline: none;
  border-color: #2d8cf0;
}

.ac-enroll__tip {
  margin: 14px 0 16px;
  font-size: 12px;
  color: #999;
}

.ac-enroll__tip a {
  color: #2d8cf0;
  font-weight: 600;
}

.ac-enroll__submit {
  width: 100%;
  padding: 12px 20px;
  border: none;
  border-radius: 4px;
  background: #ff6a00;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.ac-enroll__submit:hover {
  background: #e85f00;
}

@media (max-width: 760px) {
  .ac-enroll__row {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .ac-enroll__row--top span {
    padding-top: 0;
  }
}
</style>
